import { Truck, Clock, ShieldCheck, Star, Phone, Wrench, MapPin, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import RequestForm from "@/components/RequestForm";

const heroImg = "https://images.unsplash.com/photo-1730514784243-f0e7f09c9f50?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";

const Landing = () => {
  const scrollToForm = () => document.getElementById("aanvraag")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0">
          <img src={heroImg} alt="Takelwagen" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0B0F19]/70 via-[#0B0F19]/85 to-[#0B0F19]" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0B0F19] via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 lg:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 rounded-full bg-sky-500/10 border border-sky-500/30 px-4 py-1.5 mb-6">
              <span className="w-2 h-2 rounded-full bg-sky-400 animate-pulse-ring" />
              <span className="text-sm font-semibold text-sky-300 tracking-wide">24/7 Pechhulp & Autotransport</span>
            </div>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
              Pech onderweg?<br /><span className="text-sky-400">Wij staan klaar.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-300 leading-relaxed max-w-xl">
              Snelle en betrouwbare takeldienst, dag en nacht. Vraag online een takelverzoek aan met foto's en locatie — wij zijn er gemiddeld binnen 25 minuten.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Button onClick={scrollToForm} data-testid="hero-request-btn" className="h-12 px-7 rounded-full bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-base gap-2 glow-blue">
                Takelverzoek aanvragen <ArrowRight className="w-5 h-5" />
              </Button>
              <a href="tel:+32493967469">
                <Button variant="outline" data-testid="hero-call-btn" className="h-12 px-7 rounded-full border-slate-600 text-white hover:bg-slate-800 font-semibold text-base gap-2 w-full">
                  <Phone className="w-5 h-5" /> Direct bellen
                </Button>
              </a>
            </div>

            {/* Stats */}
            <div className="mt-12 grid grid-cols-3 gap-4 max-w-lg">
              <Stat icon={Clock} value="25 min" label="Gem. aankomsttijd" />
              <Stat icon={ShieldCheck} value="100%" label="Gecertificeerd" />
              <Stat icon={Star} value="4.9/5" label="Beoordeling" />
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section id="diensten" className="max-w-7xl mx-auto px-4 lg:px-8 py-16 lg:py-20">
        <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white text-center">Onze diensten</h2>
        <p className="text-slate-400 text-center mt-3 max-w-xl mx-auto">Van pechverhelping tot volledig voertuigtransport — één telefoontje en wij regelen het.</p>
        <div className="grid sm:grid-cols-3 gap-5 mt-10">
          <ServiceCard icon={Wrench} title="Pechhulp langs de weg" desc="Motorstoring, lege accu of klapband? Onze monteurs helpen u snel weer op weg." />
          <ServiceCard icon={Truck} title="Voertuigtransport" desc="Veilig transport van uw voertuig naar de garage of gewenste bestemming met onze oprijwagens." />
          <ServiceCard icon={MapPin} title="Ongevalberging" desc="Professionele berging na een ongeval, 24 uur per dag beschikbaar in heel de regio." />
        </div>
      </section>

      {/* REQUEST FORM */}
      <section id="aanvraag" className="relative py-16 lg:py-20 border-t border-slate-800/60 bg-slate-950/40">
        <div className="max-w-3xl mx-auto px-4 lg:px-8">
          <div className="text-center mb-10">
            <span className="text-sm font-semibold text-sky-400 tracking-[0.2em] uppercase">Online aanvraag</span>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-white mt-2">Takelverzoek aanvragen</h2>
            <p className="text-slate-400 mt-3">Vul het formulier in met de voertuiggegevens, foto's en locatie.</p>
          </div>
          <RequestForm />
        </div>
      </section>

      <footer className="border-t border-slate-800/60 py-8 text-center text-slate-500 text-sm">
        <p>© {new Date().getFullYear()} Takeldienst 24/7 · Pechhulp & Autotransport · Bel 0493967469</p>
      </footer>
    </div>
  );
};

const Stat = ({ icon: Icon, value, label }) => (
  <div className="rounded-xl bg-slate-900/50 border border-slate-800 px-3 py-4 backdrop-blur-sm">
    <Icon className="w-5 h-5 text-sky-400 mb-1.5" />
    <div className="font-heading font-bold text-white text-xl">{value}</div>
    <div className="text-xs text-slate-400">{label}</div>
  </div>
);

const ServiceCard = ({ icon: Icon, title, desc }) => (
  <div className="group rounded-2xl bg-slate-900/60 border border-slate-800 p-6 hover:border-sky-500/50 transition-colors">
    <div className="w-12 h-12 rounded-xl bg-sky-500/10 border border-sky-500/20 flex items-center justify-center mb-4 group-hover:bg-sky-500/20 transition-colors">
      <Icon className="w-6 h-6 text-sky-400" />
    </div>
    <h3 className="font-heading text-lg font-semibold text-white mb-2">{title}</h3>
    <p className="text-sm text-slate-400 leading-relaxed">{desc}</p>
  </div>
);

export default Landing;
